import { useDroppable } from '@dnd-kit/core'
import { cn, getColumns, groupChildrenByColumn, Text } from '@viapeople/components'
import type { LabelSize } from '@viapeople/components/src/components/forms/types'
import { getLabelVariant } from '@viapeople/components/src/components/forms/utils'
import { useFormBuilderStore } from '../../store'
import type { FormInputComponent } from '../../types'
import { ColumnDropZone, DropZone } from '../DropZone'
import { FormComponentWrapper } from '../formComponentWrapper/FormComponentWrapper'

interface ContainerComponentProps {
	component: FormInputComponent
}

export function ContainerComponent({ component: { id, name, children, custom } }: ContainerComponentProps) {
	const { setNodeRef, isOver, active } = useDroppable({
		id: `cdz-${id}-empty`,
		data: {
			type: 'canvas-dropzone',
			index: 0,
		},
	})
	const { getComponentDefinition, canPlaceChild } = useFormBuilderStore()

	const { isSection, label, labelColor, labelSize, labelWeight, description, descriptionColor, backgroundColor } =
		custom as {
			isSection: boolean
			label: string
			labelColor: string
			labelSize: LabelSize
			labelWeight: 'regular' | 'semibold' | 'hidden'
			description: string
			descriptionColor: string
			backgroundColor?: string
		}

	const activeComponent = active?.data.current?.component
	const canPlace = canPlaceChild(activeComponent, id)
	const componentDefinition = getComponentDefinition(name, isSection)

	// Filter out deleted children to ensure correct drop zone indices
	const visibleChildren = children?.filter((child) => child.action !== 'delete')
	const columns = getColumns(custom as Record<string, unknown>)
	return (
		<div
			key={id}
			data-container-id={id}
			style={{ backgroundColor: backgroundColor || undefined }}
			className={cn(backgroundColor && 'rounded-md')}>
			{!isSection && (
				<div className='flex flex-col gap-2 text-left m-4 mb-0 p-6 border border-neutral-200 rounded-md'>
					{label && (
						<Text
							variant={getLabelVariant(labelSize)}
							weight={(labelWeight !== 'hidden' && labelWeight) || null}
							className={cn('mb-1', labelWeight === 'hidden' && 'hidden')}
							style={{ color: labelColor }}>
							{label}
						</Text>
					)}
					{description && (
						<Text variant='small' weight='regular' style={{ color: descriptionColor }}>
							{description}
						</Text>
					)}
				</div>
			)}
			{isSection && label && (
				<div className='ml-8 mr-4 my-4 mb-0 text-left'>
					<Text
						variant={getLabelVariant(labelSize)}
						weight={(labelWeight !== 'hidden' && labelWeight) || null}
						className={cn(labelWeight === 'hidden' && 'hidden')}
						style={{ color: labelColor }}>
						{label}
					</Text>
				</div>
			)}
			{isSection && columns > 1 ? (
				<div className='grid gap-4 p-4' style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
					{groupChildrenByColumn(visibleChildren || [], columns).map((colChildren, colIdx) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: columns are positional with no stable identifier
						<div key={colIdx} className='space-y-2 min-h-[60px]'>
							{colChildren.length === 0 ? (
								<ColumnDropZone id={`cdz-${id}-col${colIdx}-0`} containerId={id} index={0} colIdx={colIdx} />
							) : (
								<>
									<DropZone id={`cdz-${id}-col${colIdx}-0`} containerId={id} index={0} />
									{colChildren.map((child, childIdx) => (
										// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
										<div
											key={child.id}
											className='mb-2 space-y-2'
											data-child-index={childIdx}
											role='presentation'
											onClick={(e) => e.stopPropagation()}
											onKeyDown={(e) => e.stopPropagation()}>
											<FormComponentWrapper component={child} index={childIdx} containerId={id} />
											<DropZone id={`cdz-${id}-col${colIdx}-${childIdx + 1}`} containerId={id} index={childIdx + 1} />
										</div>
									))}
								</>
							)}
						</div>
					))}
				</div>
			) : (visibleChildren && visibleChildren.length > 0) || !isSection ? (
				<div className='space-y-2 p-4'>
					<DropZone id={`cdz-${id}-0`} containerId={id} index={0} />
					{visibleChildren?.map((child, index) => (
						// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
						<div
							key={child.id}
							className='mb-2 space-y-2'
							data-child-index={index}
							role='presentation'
							onClick={(e) => e.stopPropagation()}
							onKeyDown={(e) => e.stopPropagation()}>
							<FormComponentWrapper component={child} index={index} containerId={id} />
							<DropZone id={`cdz-${id}-${index + 1}`} containerId={id} index={index + 1} />
						</div>
					))}
				</div>
			) : (
				<div
					ref={setNodeRef}
					className={`h-50 bg-neutral-50 rounded-md m-6 flex items-center justify-center ${
						!canPlace && isOver ? 'border border-error-600' : isOver && 'border border-primary-600'
					}`}>
					<Text variant='small' className='text-neutral-400'>
						{!canPlace && isOver
							? "You can't add this element here"
							: `Drag elements here to include in this ${componentDefinition?.displayName}`}
					</Text>
				</div>
			)}
		</div>
	)
}
